import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, EyeOff, Globe, Inbox, Loader2, Plus, X } from 'lucide-react';
import type { PartnerListItem } from '@proptr/shared';
import { ApiError } from '@proptr/api-client';
import { AppShell } from '../components/layout/AppShell.js';
import { PartnerButtonPreview } from '../components/PartnerButtonPreview.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { client } from '../main.js';

/**
 * Partner buttons — branded call-to-actions agencies and agents can
 * switch on for their proptrees. Each opens the partner's own form, and
 * each submission is POSTed straight to the partner's endpoint.
 */
export function PartnersPage() {
  const [items, setItems] = React.useState<PartnerListItem[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    client.admin.partners
      .list()
      .then((res) => alive && setItems(res.items))
      .catch((err: Error) => alive && setError(err.message));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <AppShell>
      <div className="space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="eyebrow">Admin / Partners</div>
            <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
              Partners
            </h1>
            <p className="mt-3 font-mono text-[11px] text-muted-foreground">
              Branded buttons agencies and agents can switch on. Leads from a
              partner’s form go straight to the partner.
            </p>
          </div>
          <Button onClick={() => setCreating(true)}>
            <Plus />
            New partner
          </Button>
        </div>

        {error && (
          <Card className="border-destructive/40 bg-destructive/10 p-4 font-mono text-[11px] text-destructive">
            {error}
          </Card>
        )}

        {!items && !error && (
          <Card className="p-6 font-mono text-[11px] text-muted-foreground">
            Loading…
          </Card>
        )}

        {items && items.length === 0 && (
          <Card className="flex flex-col items-center gap-3 p-10 text-center">
            <Inbox className="h-6 w-6 text-muted-foreground" />
            <p className="max-w-sm text-[13px] text-muted-foreground">
              No partners yet. Create one to give agencies a branded partner
              button with its own form.
            </p>
            <Button variant="outline" onClick={() => setCreating(true)}>
              <Plus />
              New partner
            </Button>
          </Card>
        )}

        {items && items.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {items.map((p) => (
              <PartnerCard key={p._id} partner={p} />
            ))}
          </div>
        )}
      </div>
      {creating && <CreatePartnerDialog onClose={() => setCreating(false)} />}
    </AppShell>
  );
}

function PartnerCard({ partner: p }: { partner: PartnerListItem }) {
  const host = endpointHost(p.leadEndpoint);
  return (
    <Link
      to={`/partners/${p._id}`}
      className="group rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <Card className="flex h-full flex-col p-0 transition-shadow group-hover:shadow-pop">
        <div className="border-b border-[hsl(var(--hairline))] bg-muted/40 p-4">
          <PartnerButtonPreview
            name={p.name}
            subtitle={p.subtitle}
            color={p.color}
            logo={p.logo}
            ctaLabel={p.ctaLabel}
          />
        </div>
        <div className="flex flex-1 flex-col gap-2.5 p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate font-display text-[18px] font-medium leading-tight">
                {p.name}
              </div>
              <div className="font-mono text-[10px] text-muted-foreground">{p.key}</div>
            </div>
            <span className={p.active ? 'pill pill-success' : 'pill pill-muted'}>
              {p.active ? 'Live' : 'Paused'}
            </span>
          </div>
          <div className="mt-auto space-y-1.5 border-t border-[hsl(var(--hairline))] pt-3 font-mono text-[11px]">
            <div className="flex items-center gap-2 text-muted-foreground">
              {host ? (
                <>
                  <Globe className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">Leads → {host}</span>
                </>
              ) : (
                <span className="flex items-center gap-2 text-warning">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  No lead endpoint — leads are kept, not sent
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <EyeOff className="h-3.5 w-3.5 shrink-0" />
              {p.excludedEnterprises.length === 0
                ? 'Available to every agency'
                : `Hidden for ${p.excludedEnterprises.length} ${p.excludedEnterprises.length === 1 ? 'agency' : 'agencies'}`}
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Inbox className="h-3.5 w-3.5 shrink-0" />
              {p.stats.leads} {p.stats.leads === 1 ? 'lead' : 'leads'} ·{' '}
              {p.stats.delivered} delivered
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function CreatePartnerDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = React.useState('');
  const [color, setColor] = React.useState('#2563EB');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const p = await client.admin.partners.create({ name: name.trim(), color });
      navigate(`/partners/${p._id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <Card className="relative w-full max-w-md p-0 shadow-pop">
        <form onSubmit={submit}>
          <div className="flex items-start justify-between border-b border-[hsl(var(--hairline))] px-5 py-4">
            <div>
              <h2 className="font-display text-[20px] font-medium leading-none">New partner</h2>
              <p className="mt-1.5 text-[12px] text-muted-foreground">
                You’ll set the logo, lead endpoint and form next.
              </p>
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close">
              <X />
            </Button>
          </div>
          <div className="space-y-4 px-5 py-5">
            <div>
              <Label htmlFor="partner-name">Name</Label>
              <Input
                id="partner-name"
                className="mt-1"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Mortgage Finder"
                autoFocus
                maxLength={60}
              />
            </div>
            <div>
              <Label htmlFor="partner-color">Brand colour</Label>
              <ColorField id="partner-color" value={color} onChange={setColor} />
            </div>
            <PartnerButtonPreview
              name={name}
              subtitle={null}
              color={/^#[0-9a-f]{6}$/i.test(color) ? color : '#2563EB'}
              logo={null}
              ctaLabel="More info"
            />
            {error && <p className="text-[12px] text-destructive">{error}</p>}
          </div>
          <div className="flex justify-end gap-2 border-t border-[hsl(var(--hairline))] px-5 py-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !name.trim() || !/^#[0-9a-f]{6}$/i.test(color)}>
              {busy && <Loader2 className="animate-spin" />}
              Create partner
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

/** Native colour picker + hex input, kept in sync. */
export function ColorField({
  id,
  value,
  onChange,
}: {
  id?: string;
  value: string;
  onChange: (hex: string) => void;
}) {
  const valid = /^#[0-9a-f]{6}$/i.test(value);
  return (
    <div className="mt-1 flex items-center gap-2">
      <input
        type="color"
        aria-label="Pick colour"
        value={valid ? value : '#000000'}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="h-11 w-14 shrink-0 cursor-pointer rounded-md border border-border bg-card p-1 dark:border-white/10"
      />
      <Input
        id={id}
        value={value}
        onChange={(e) => {
          const v = e.target.value.trim();
          onChange(v.startsWith('#') ? v : `#${v}`);
        }}
        className={valid ? 'font-mono' : 'border-destructive font-mono'}
        maxLength={7}
        placeholder="#2563EB"
      />
    </div>
  );
}

export function endpointHost(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
